<template>
    <div v-if="scenario" class="space-y-4">
        <HeadlessCard :invisible-headline="$t('changes')">
            <div class="p-4">
                <Feed>
                    <AddDateCommentEntry
                        @input="
                            (event: Event) => handleAddDateCommentEntryInput((event.target as HTMLInputElement).value)
                        "
                        @create-comment="onCreateComment"
                    />
                    <template v-for="entry in feedEntries" :key="entry.id">
                        <DateCommentEntry
                            v-if="entry.type === 'user-text'"
                            :text="entry.text"
                            image-url=""
                            :first-name="entry.createdBy!.firstName"
                            :last-name="entry.createdBy!.lastName"
                            :user-page-link="{ name: 'user-details', params: { userId: entry.createdBy!.id } }"
                            :date-time="DateTime.fromISO(entry.createdAt)"
                            :is-editable="false"
                            has-unsaved-changes="None"
                        />
                        <StatusChangeEntry
                            v-if="entry.type === 'new-status'"
                            :new-status="getScenarioStatusLabel(entry.newStatus)"
                            :previous-status="getScenarioStatusLabel(entry.oldStatus)"
                            :date-time="DateTime.fromISO(entry.createdAt)"
                            :user-name="entry.createdByName"
                            :user-page-link="{ name: 'user-details', params: { userId: entry.createdBy!.id } }"
                        />
                        <CreationDateEntry
                            v-if="entry.type === 'created-at'"
                            :user-name="entry.createdByName"
                            :user-page-link="{ name: 'user-details', params: { userId: entry.createdBy!.id } }"
                            :date-time="DateTime.fromISO(entry.objectCreatedAt ?? '')"
                        />
                    </template>
                </Feed>
            </div>
        </HeadlessCard>
    </div>
</template>
<script setup lang="ts">
import HeadlessCard from '@/components/Card/HeadlessCard.vue';
import AddDateCommentEntry from '@/components/Feed/AddDateCommentEntry.vue';
import CreationDateEntry from '@/components/Feed/CreationDateEntry.vue';
import DateCommentEntry from '@/components/Feed/DateCommentEntry.vue';
import Feed from '@/components/Feed/Feed.vue';
import StatusChangeEntry from '@/components/Feed/StatusChangeEntry.vue';
import { feedData, scenarioData } from '@/scenario/keys';
import { getScenarioStatusLabel } from '@/scenario/scenario';
import { useHeadSafe } from '@unhead/vue';
import { useFluent } from 'fluent-vue';
import { DateTime } from 'luxon';
import { computed, inject, ref } from 'vue';

const scenarioInject = inject(scenarioData);
const scenario = computed(() => scenarioInject?.scenario.value);

const feedInject = inject(feedData);
const feedEntries = computed(() => feedInject?.feedEntries.value ?? []);

const fluent = useFluent();

const newEntry = ref('');

function handleAddDateCommentEntryInput(value: string) {
    newEntry.value = value;
}

async function onCreateComment(text: string, onDone: (error?: Error) => void) {
    feedInject?.createUserTextEntry(text);
    // TODO Implement correctly
    onDone();
}

useHeadSafe({
    title: () =>
        `${fluent.$t('changes')} - ${scenario.value?.nameShort} - ${
            scenario.value?.scenarioGroup?.nameShort
        } - ${fluent.$t('scenarios')}`,
});
</script>
